import { App, Button, Flex, Typography } from "antd";
import { FC, useState } from "react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { PageId, PageType } from "@/types/page";
import { Requirement, RequirementConfirmShowEnum, RequirementConfirmValueEnum, RequirementItemDelEnum, RequirementStatusEnum } from "@/types/requirement";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import useEditRequirementProgress from "@/pages/Requirement/hooks/useEditRequirementProgress";
import useChangeRequirementStatus from "@/pages/Requirement/hooks/useChangeRequirementStatus";
import { RequirementDetailType } from "../hooks/useGetDetail";
import refreshIcon from "@/assets/images/common/refresh.svg";
import RequirementQuotation from "./RequirementQuotation";
import usePage from "@/layout/hooks/usePage";
import PrintRequirement from "@/pages/Requirement/components/PrintRequirement";
import RejectModal from "./RejectModal";
import * as orderApis from "@/apis/order";
import usePassed from "@/pages/Sales/hooks/usePassed";
import useDataUpdate from "@/hooks/useDataUpdate";
import { UpdateByKey } from "@/apis/order";
import { OutQtyObj } from "./PreOutOrderPrint";
interface DetailTitleProps {
  pageId: PageId | undefined;
  detail: Requirement | undefined;
  id: string;
  confirmValue: RequirementConfirmValueEnum | undefined;
  handleOk: (type?: 'close' | 'del') => void;
  onUpdatePurCount: () => void;
  items: Array<RequirementDetailType>;
  passLoading: boolean;
  handlePurchase: (ids: Array<string>) => void;
  onRefresh?: () => void;
  reOrder?: boolean;
  canPrint?: boolean;
  preOutQtyMap?: Record<string, OutQtyObj>
  onDeleteAllItems?: () => Promise<void>;
}

const DetailTitle: FC<DetailTitleProps> = ({
  pageId, detail, id, confirmValue, handleOk, onUpdatePurCount, items, passLoading, handlePurchase, onRefresh, reOrder, canPrint = false, preOutQtyMap, onDeleteAllItems
}) => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();
  const { pageType } = usePage();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [delSaleLoading, setDelSaleLoading] = useState(false);

  const [editRequirement, editLoading] = useEditRequirementProgress(id, detail?.confirms ?? [])
  const [changeRequirementDoneStatus, doneLoading] = useChangeRequirementStatus();
  const [changeRequirementStatus, closeLoading] = useChangeRequirementStatus();

  const { update } = useDataUpdate<[string, UpdateByKey]>({ api: orderApis.updateByKey });

  const { passed, loading } = usePassed({
    setId: () => { },
    updateApi: update,
    handleOk: () => { handleOk('del'); },
    errorTip: t('sale.passError')
  })

  const handleCloseRequirementSuccess = () => {
    if (detail?.saleOrder?.id) {
      setDelSaleLoading(true);
      orderApis.updateByKey(detail.saleOrder.id, { key: "preApproval", value: 0 })
        .then(() => { })
        .catch(() => { })
        .finally(() => {
          setDelSaleLoading(false);
          handleOk?.('del')
        })
    } else {
      handleOk && handleOk('del');
    }
  };

  const closeRequirement = async () => {
    await changeRequirementStatus(id, RequirementStatusEnum.CLOSE, handleCloseRequirementSuccess);
  };

  const handleSyncClearItemsAndClose = (closeRequirementModal: ReturnType<typeof modal.confirm>) => {
    modal.confirm({
      title: t("common.tip"),
      content: t("requirement.syncClearRequirementItemsTip"),
      okText: t("common.confirm"),
      cancelText: t("common.cancel"),
      onOk: async () => {
        await onDeleteAllItems?.();
        // 二次确认成功后再关闭外层弹窗，取消时保留原关闭确认弹窗。
        closeRequirementModal.destroy();
        await closeRequirement();
      },
    });
  };

  const handleCloseRequirement = async () => {
    const closeRequirementModal = modal.confirm({
      title: t('common.tip'),
      content: t('requirement.closeTip'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      footer: (_, { OkBtn, CancelBtn }) => (
        <Flex justify="flex-end" gap={8}>
          <Button type="primary" onClick={() => {
            handleSyncClearItemsAndClose(closeRequirementModal);
          }}>
            {t("requirement.syncClearRequirementItems")}
          </Button>
          <CancelBtn />
          <OkBtn />
        </Flex>
      ),
      onOk: async () => {
        await closeRequirement();
      },
    });
  };
  const { onConfirm: onDoneRequirement } = useFrontDelTip<void, [id: string, status: RequirementStatusEnum, successCallback: () => void]>({
    cb: (...args) => {
      return changeRequirementDoneStatus(...args)
    },
    message: t('common.tip'),
    desc: t('requirement.endError'),
  });

  const handleReject = (desc: string) => {
    return new Promise<void>((resolve, reject) => {
      editRequirement('prev', desc, () => {
        handleOk && handleOk('del');
        resolve();
      }).catch(reject);
    });
  };

  const getConfirmBtn: () => boolean = () => {
    const showPageExpectSale: Array<PageId> = [PageId.MC_NOT_PASS, PageId.COMMODITY_NOT_PASS, PageId.PROD_NOT_PASS, PageId.PUR_NOT_PASS, PageId.F_NOT_PASS];
    if (pageId && showPageExpectSale.includes(pageId) && confirmValue === RequirementConfirmValueEnum.NOT_PASS) {
      return true
    }
    if (pageId === PageId.MC_BE_REJECT) {
      return true
    }
    const showProgress = detail?.confirms?.filter(confirm => confirm?.show !== RequirementConfirmShowEnum.HIDDEN) ?? [];
    const passProgress = showProgress?.filter(confirm => confirm?.value === RequirementConfirmValueEnum.PASSED) ?? [];
    if (showProgress.length - 1 === passProgress.length && pageId === PageId.SALE_NOT_PASS) {
      return true
    }
    return false
  }

  const salePass = () => {
    passed(detail?.saleOrder!);
  };

  return (
    <Fragment>
      <Flex justify="space-between">
        <Flex gap={10}>
          {pageId === PageId.F_NOT_PASS ? t("finance.priceCheck") : t("requirement.detail")}
          <Typography.Link>{detail?.number}</Typography.Link>
          <Flex gap={10} justify="flex-end">
            {reOrder ? (<></>) : (
              <>
                {pageId === PageId.PUR_ALL_PASSED && (
                  <Button type="primary" onClick={() => {
                    notification.error({ placement: 'top', message: t('requirement.noPurchaseTip') })
                  }}>
                    {t("requirement.purchase")}
                  </Button>
                )}
                {detail?.status === RequirementStatusEnum.OPEN_PENDING && getConfirmBtn() && (
                  <>
                    {pageId !== PageId.MC_BE_REJECT && <Button loading={editLoading} type="link" danger onClick={() => setRejectModalOpen(true)}>
                      {t("requirement.reject")}
                    </Button>}
                    <Button type="primary" loading={passLoading} onClick={onUpdatePurCount}>
                      {t("common.pass")}
                    </Button>
                  </>
                )}
                {
                  pageId && detail?.status === RequirementStatusEnum.OPEN_PENDING && [PageId.PROD_BE_REJECT, PageId.PUR_BE_REJECT, PageId.F_BE_REJECT].includes(pageId) && <Button type="primary" loading={passLoading} onClick={onUpdatePurCount}>
                    {t("common.pass")}
                  </Button>
                }
                {/* {detail?.status === RequirementStatusEnum.OPEN_PENDING && <Button loading={doneLoading} type="default" onClick={() => onDoneRequirement(id, RequirementStatusEnum.OPEN_DONE, () => handleOk && handleOk('close'))}>
                    {t("requirement.done")}
                  </Button>} */}
                {pageId && [PageId.MC_REQ_MANAGE].includes(pageId) && detail?.status !== RequirementStatusEnum.CLOSE && <Button loading={closeLoading || delSaleLoading} type="primary" onClick={handleCloseRequirement}>
                  {t("requirement.closeReq")}
                </Button>}
              </>
            )}
            {pageId && pageId === PageId.SALE_NOT_PASS
              && !!!detail?.confirms.find(confirm => confirm.show === RequirementConfirmShowEnum.SHOW && confirm.value !== RequirementConfirmValueEnum.PASSED)
              && (
                <Button onClick={salePass} loading={loading}>{t("requirement.salePass")}</Button>
              )}
            {pageId && [PageId.PUR_NOT_PASS].includes(pageId) && <RequirementQuotation requirementId={detail?.id || ''} />}
          </Flex>
          {canPrint && detail && <PrintRequirement requirement={detail}
            reqItems={items?.filter(item => item.deleted !== RequirementItemDelEnum.DEL) ?? []}
            disabled={!!!items?.filter(i => i.deleted !== RequirementItemDelEnum.DEL)?.length} preOutQtyMap={preOutQtyMap}
          />}
        </Flex>
        {onRefresh && <Button type="text" icon={<img src={refreshIcon} alt="" />} onClick={onRefresh} style={{ marginRight: "30px", marginTop: 2 }} />}
      </Flex>
      {rejectModalOpen && <RejectModal
        open={rejectModalOpen}
        onConfirm={handleReject}
        onCancel={() => setRejectModalOpen(false)}
        loading={editLoading}
      />}
    </Fragment>
  )
}

export default DetailTitle;
